import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DownloadFileCard } from './ai-chat-file-card'
import { useChatFileDownload } from './use-chat-file-download'
import { useFileOperationPolling } from './use-file-operation-polling'
import type { LiveAiChatMessage } from '../ai-chat-live-content'
import { DocumentPreviewDialog } from '@/features/document-preview/document-preview-dialog'
import { isPreviewableFileName } from '@/features/document-preview/preview-format'
import {
  AiChatFileReferenceKind,
  ChatFileStatus,
  DocumentPreviewTargetKind,
  FileOperationStatus,
} from '@/graphql/schema'
import { useChatFileQuery } from '@/graphql/queries/chatFileQuery.gql.generated'

const OPERATION_TOOLS = new Set([
  'files_import',
  'files_create',
  'files_edit',
  'attachments_attach',
  'attachments_replace',
])

type ChatFile = NonNullable<LiveAiChatMessage['files']>[number]
type Downloads = ReturnType<typeof useChatFileDownload>
type Preview = {
  kind: 'chat-file' | 'attachment'
  id: string
  fileName: string
}
type OnPreview = (preview: Preview) => void

const previewIf = (preview: Preview, onPreview: OnPreview) =>
  isPreviewableFileName(preview.fileName) ? () => onPreview(preview) : undefined

function ChatFileCard({
  file,
  downloads,
  onPreview,
}: {
  file: ChatFile
  downloads: Downloads
  onPreview: OnPreview
}) {
  const { t } = useTranslation()
  const ready = file.status === ChatFileStatus.Ready
  const failed = file.status === ChatFileStatus.Failed

  return (
    <DownloadFileCard
      fileName={file.fileName}
      size={file.size}
      status={t(`aiChat:files.status.${file.status.toLowerCase()}`)}
      detail={failed ? t('aiChat:files.failures.generic') : null}
      busy={downloads.busy === file.id}
      onPreview={
        ready
          ? previewIf(
              { kind: 'chat-file', id: file.id, fileName: file.fileName },
              onPreview,
            )
          : undefined
      }
      onDownload={
        ready
          ? () =>
              void downloads.download({
                kind: 'chat-file',
                id: file.id,
                fileName: file.fileName,
              })
          : undefined
      }
    />
  )
}

function ResultFileCard({
  fileId,
  downloads,
  onPreview,
}: {
  fileId: string
  downloads: Downloads
  onPreview: OnPreview
}) {
  const { data } = useChatFileQuery({ variables: { id: fileId } })

  return data ? (
    <ChatFileCard
      file={data.chatFile}
      downloads={downloads}
      onPreview={onPreview}
    />
  ) : null
}

function operationDetail(
  operation: {
    status: FileOperationStatus
    message?: string | null
    errorMessage?: string | null
    errorCode?: string | null
  },
  pending: string,
) {
  if (operation.status === FileOperationStatus.Succeeded)
    return operation.message
  if (operation.status === FileOperationStatus.Failed)
    return operation.errorMessage ?? operation.errorCode

  return pending
}

function OperationCards({
  id,
  shown,
  downloads,
  onPreview,
}: {
  id: string
  shown: ReadonlySet<string>
  downloads: Downloads
  onPreview: OnPreview
}) {
  const { t } = useTranslation()
  const operation = useFileOperationPolling(id)
  if (!operation) return null
  const succeeded = operation.status === FileOperationStatus.Succeeded
  const attachmentId = succeeded ? operation.attachmentId : null
  const resultFileId = succeeded ? operation.resultFileId : null

  return (
    <>
      <DownloadFileCard
        fileName={t(`aiChat:files.operation.${operation.kind.toLowerCase()}`)}
        status={t(
          `aiChat:files.operationStatus.${operation.status.toLowerCase()}`,
        )}
        detail={operationDetail(operation, t('aiChat:files.operationPending'))}
      />
      {resultFileId && !shown.has(resultFileId) ? (
        <ResultFileCard
          fileId={resultFileId}
          downloads={downloads}
          onPreview={onPreview}
        />
      ) : null}
      {attachmentId ? (
        <DownloadFileCard
          fileName={t('aiChat:files.recordAttachment')}
          busy={downloads.busy === attachmentId}
          onPreview={() =>
            onPreview({
              kind: 'attachment',
              id: attachmentId,
              fileName: t('aiChat:files.recordAttachment'),
            })
          }
          onDownload={() =>
            void downloads.download({
              kind: 'attachment',
              id: attachmentId,
              fileName: t('aiChat:files.recordAttachment'),
            })
          }
        />
      ) : null}
    </>
  )
}

function referencedIds(message: LiveAiChatMessage) {
  const operations = new Set<string>()
  const attachments = new Map<string, string | null>()
  for (const call of message.toolCalls)
    for (const reference of call.references ?? []) {
      if (
        reference.kind === AiChatFileReferenceKind.FileOperation &&
        OPERATION_TOOLS.has(call.toolName)
      )
        operations.add(reference.id)
      if (
        reference.kind === AiChatFileReferenceKind.Attachment &&
        call.toolName === 'attachments_download'
      )
        attachments.set(reference.id, reference.fileName ?? null)
    }

  return { operations, attachments }
}

function ChatPreview({
  preview,
  downloads,
  onClose,
}: {
  preview: Preview
  downloads: Downloads
  onClose: () => void
}) {
  return (
    <DocumentPreviewDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      target={{
        kind:
          preview.kind === 'chat-file'
            ? DocumentPreviewTargetKind.ChatFile
            : DocumentPreviewTargetKind.Attachment,
        id: preview.id,
      }}
      fileName={preview.fileName}
      downloadBusy={downloads.busy === preview.id}
      downloadError={downloads.error}
      onDownload={() => void downloads.download(preview)}
    />
  )
}

function MessageFileList({
  files,
  operations,
  attachments,
}: {
  files: NonNullable<LiveAiChatMessage['files']>
  operations: ReadonlySet<string>
  attachments: ReadonlyMap<string, string | null>
}) {
  const { t } = useTranslation()
  const downloads = useChatFileDownload()
  const [previewing, setPreviewing] = useState<Preview | null>(null)
  const shown = new Set(files.map((file) => file.id))

  return (
    <div className="mt-3 space-y-2">
      <ul aria-label={t('aiChat:files.list')} className="space-y-2">
        {files.map((file) => (
          <ChatFileCard
            key={file.id}
            file={file}
            downloads={downloads}
            onPreview={setPreviewing}
          />
        ))}
        {[...attachments].map(([id, name]) => (
          <DownloadFileCard
            key={id}
            fileName={name ?? t('aiChat:files.recordAttachment')}
            busy={downloads.busy === id}
            onPreview={() =>
              setPreviewing({
                kind: 'attachment',
                id,
                fileName: name ?? t('aiChat:files.recordAttachment'),
              })
            }
            onDownload={() =>
              void downloads.download({
                kind: 'attachment',
                id,
                fileName: name ?? t('aiChat:files.recordAttachment'),
              })
            }
          />
        ))}
        {[...operations].map((id) => (
          <OperationCards
            key={id}
            id={id}
            shown={shown}
            downloads={downloads}
            onPreview={setPreviewing}
          />
        ))}
      </ul>
      {downloads.error && !previewing ? (
        <p role="alert" className="text-xs text-destructive">
          {downloads.error}
        </p>
      ) : null}
      {previewing ? (
        <ChatPreview
          preview={previewing}
          downloads={downloads}
          onClose={() => setPreviewing(null)}
        />
      ) : null}
    </div>
  )
}

/** Cards a message shows: its files and the outcome of file operations. */
export function AiChatMessageFiles({
  message,
}: {
  message: LiveAiChatMessage
}) {
  const files = message.files ?? []
  const { operations, attachments } = referencedIds(message)
  if (!files.length && !operations.size && !attachments.size) return null

  return (
    <MessageFileList
      files={files}
      operations={operations}
      attachments={attachments}
    />
  )
}
